import React, { useMemo } from "react";
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { OverflowInfoCell } from "./OverflowInfoCell";
import { SearchableFilterSelect } from "./SearchableFilterSelect";
import { SortIcon } from "./SortIcon";
import { useVirtualizedRows } from "./hooks/useVirtualizedRows";

export const LeavesTab: React.FC<any> = ({
  loading,
  searchQuery,
  setSearchQuery,
  assigneeFilter,
  setAssigneeFilter,
  uniqueAssignees,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  hasActiveFilters,
  clearAllFilters,
  tableCardRef,
  tableCardStyle,
  handleActivitySort,
  activitySortField,
  activitySortOrder,
  leaveRowFilters,
  setLeaveRowFilters,
  leaveSubmitDescriptionLabel,
  paginatedReports,
  getReportStartEnd,
  getReportDuration,
  getReportLeaveType,
  getReportSubmitDescription,
  latestManagerCommentsByActivityId,
  formatDateOnlyTr,
  formatTimeOnlyTr,
  formatHoursTr,
  activityPageStartIndex,
  activityTabReports,
  leaveSummaryMode,
  setLeaveSummaryMode,
  filteredLeavesAverageDuration,
  filteredLeavesTotalDuration,
  t,
  isAdmin,
  isManager,
  openManagerCommentModal,
  handleViewDetails,
  handleOpenDeleteActivityConfirm,
  handleApproveActivity,
  handleRejectActivity,
  activityTotalPages,
  activityCurrentPage,
  setActivityCurrentPage,
  exportActivityTabToExcel,
  activityItemsPerPage,
  setActivityItemsPerPage,
  reports,
}) => {
  const {
    scrollContainerRef,
    setScrollTop,
    virtualizationEnabled,
    startIndex: virtualStartIndex,
    endIndex: virtualEndIndex,
    topSpacerHeight,
    bottomSpacerHeight,
  } = useVirtualizedRows({
    itemCount: paginatedReports.length,
    rowHeight: 40,
    overscan: 10,
    enabled: !loading && paginatedReports.length > 80,
    resetKey: `${activityCurrentPage}-${activityItemsPerPage}-${paginatedReports.length}`,
  });

  const visibleReports = useMemo(
    () =>
      virtualizationEnabled
        ? paginatedReports.slice(virtualStartIndex, virtualEndIndex)
        : paginatedReports,
    [paginatedReports, virtualEndIndex, virtualStartIndex, virtualizationEnabled],
  );

  const labels = useMemo(
    () => ({
      searchPlaceholder: t("dashboard:managerLeaves.searchPlaceholder"),
      employeeName: t("dashboard:managerLeaves.employeeName"),
      employee: t("dashboard:managerLeaves.employee"),
      rowNo: t("dashboard:managerLeaves.rowNo"),
      leaveStartDate: t("dashboard:managerLeaves.leaveStartDate"),
      leaveEndDate: t("dashboard:managerLeaves.leaveEndDate"),
      leaveAssignee: t("dashboard:managerLeaves.leaveAssignee"),
      leaveStartTime: t("dashboard:managerLeaves.leaveStartTime"),
      leaveEndTime: t("dashboard:managerLeaves.leaveEndTime"),
      durationHours: t("dashboard:managerLeaves.durationHours"),
      leaveType: "İzin Türü",
      managerComment: t("dashboard:managerLeaves.managerComment"),
      action: t("dashboard:managerLeaves.action"),
      sort: t("dashboard:managerLeaves.sort"),
      personPlaceholder: t("dashboard:managerLeaves.personPlaceholder"),
      minHoursPlaceholder: t("dashboard:managerLeaves.minHoursPlaceholder"),
      loadingData: t("dashboard:managerLeaves.loadingData"),
      leaveTypeTitle: "İzin Türü",
      comment: t("dashboard:managerLeaves.comment"),
      summaryTotal: t("dashboard:managerLeaves.summaryTotal"),
      summaryAverage: t("dashboard:managerLeaves.summaryAverage"),
      excelDownload: t("dashboard:managerLeaves.excelDownload"),
      showAll: t("dashboard:managerLeaves.showAll"),
      recordsMore: (count: number) =>
        t("dashboard:managerLeaves.recordsMore", { count }),
      recordCount: (count: number) =>
        t("dashboard:managerLeaves.recordCount", { count }),
      recordsFound: (count: number) =>
        t("dashboard:managerLeaves.recordsFound", { count }),
      totalRecordsFrom: (total: number) =>
        t("dashboard:managerLeaves.totalRecordsFrom", { total }),
    }),
    [t],
  );
  const assigneeOptions = useMemo(
    () => [
      { value: "all", label: labels.employee },
      ...uniqueAssignees.map((assignee: string) => ({
        value: assignee,
        label: assignee,
      })),
    ],
    [labels.employee, uniqueAssignees],
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="relative z-[60] bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-visible">
          <div className="bg-slate-50 border-b-2 border-gray-200 px-2.5 py-2 overflow-visible">
            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-1 sm:overflow-x-auto lg:overflow-visible">
              <div className="relative w-[146px] xl:w-[168px] min-w-[126px] shrink-0">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-indigo-400" />
                <input
                  type="text"
                  placeholder={labels.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-2 py-1.5 text-[13px] bg-white/80 backdrop-blur-sm border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400 shadow-sm transition-all font-medium"
                />
              </div>
              <div className="hidden lg:block h-8 w-px bg-gradient-to-b from-indigo-200 via-purple-300 to-pink-200 shrink-0" />
              <SearchableFilterSelect
                value={assigneeFilter}
                onChange={setAssigneeFilter}
                options={assigneeOptions}
                searchPlaceholder={t("common:search")}
                emptyLabel={t("common:noResults")}
                title={labels.employeeName}
                containerClassName="w-[136px] xl:w-[148px] min-w-[136px]"
                buttonClassName="border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-400 font-semibold text-amber-700 hover:bg-amber-50"
              />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-[100px] xl:w-[112px] px-1 py-1.5 text-[13px] border-2 border-pink-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-400 shadow-sm font-semibold text-pink-700 transition-all hover:bg-pink-50 shrink-0"
                placeholder={t("dashboard:startDate")}
                title={t("dashboard:startDate")}
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-[100px] xl:w-[112px] px-1 py-1.5 text-[13px] border-2 border-rose-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-400 shadow-sm font-semibold text-rose-700 transition-all hover:bg-rose-50 shrink-0"
                placeholder={t("dashboard:endDate")}
                title={t("dashboard:endDate")}
              />
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1 px-1.5 py-1.5 text-[13px] border-2 border-red-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-red-500 font-semibold text-red-600 transition-all hover:bg-red-50 shadow-sm whitespace-nowrap shrink-0"
                >
                  <XMarkIcon className="w-4 h-4" />
                  <span className="hidden xl:inline">{t("common:clear")}</span>
                </button>
              )}
            </div>
          </div>
      </div>
      <div
        ref={tableCardRef}
        className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-hidden flex flex-col"
        style={tableCardStyle}
      >
        <div
          ref={scrollContainerRef}
          onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
          className="overflow-auto min-h-0 flex-1"
        >
          <div className="space-y-3 p-3 md:hidden">
            {loading ? (
              <div className="flex items-center justify-center gap-3 py-8 text-sm text-red-700">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-red-300 border-t-red-700" />
                {labels.loadingData}
              </div>
            ) : paginatedReports.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                {t("dashboard:noActivitiesFound")}
              </div>
            ) : (
              visibleReports.map((report: any, idx: number) => {
                const { start, end } = getReportStartEnd(report);
                const duration = getReportDuration(report);
                const leaveType = getReportLeaveType(report);
                const description = getReportSubmitDescription(report);
                const latestComment =
                  latestManagerCommentsByActivityId[String(report.activityId)];
                const rowNo = activityPageStartIndex + virtualStartIndex + idx + 1;
                const canManageActivity = isAdmin() || isManager();
                const isPendingApproval =
                  report.activityStatus === "PENDING_APPROVAL" &&
                  canManageActivity;

                return (
                  <div
                    key={`mobile-leave-${report.activityId}-${idx}`}
                    className={`rounded-lg border px-3 py-2 shadow-sm ${
                      isPendingApproval
                        ? "border-amber-200 bg-amber-50/70"
                        : "border-red-100 bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-6 min-w-6 items-center justify-center rounded-md bg-red-50 text-[11px] font-bold text-red-700">
                        #{rowNo}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleViewDetails(report)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <p className="truncate text-[13px] font-bold text-gray-900">
                            {report.activityTitle || leaveType.summary || "-"}
                          </p>
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                              isPendingApproval
                                ? "bg-amber-100 text-amber-700"
                                : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {report.activityStatus || "-"}
                          </span>
                        </div>
                        <p className="mt-0.5 truncate text-[11px] font-medium text-gray-500">
                          {report.assigneeName || report.userName || "-"} -{" "}
                          {leaveType.summary || "-"}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] font-semibold text-slate-600">
                          <span>
                            {formatDateOnlyTr(start)} {formatTimeOnlyTr(start)}
                          </span>
                          <span>
                            {formatDateOnlyTr(end)} {formatTimeOnlyTr(end)}
                          </span>
                          <span>
                            {duration > 0 ? formatHoursTr(duration) : "-"}{" "}
                            {t("dashboard:hours")}
                          </span>
                        </div>
                        {(description || latestComment?.comment) && (
                          <p className="mt-1 truncate text-[11px] text-gray-500">
                            {description || latestComment?.comment}
                          </p>
                        )}
                      </button>
                      <div className="flex shrink-0 items-center gap-1">
                        {canManageActivity && (
                          <button
                            onClick={() => openManagerCommentModal(report)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-700"
                            title={labels.comment}
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleViewDetails(report)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700"
                          title={t("common:view")}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {canManageActivity && (
                          <button
                            onClick={() => handleOpenDeleteActivityConfirm(report)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-red-700"
                            title={t("common:delete")}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {isPendingApproval && (
                      <div className="mt-2 grid grid-cols-2 gap-2 border-t border-amber-100 pt-2">
                        <button
                          onClick={() => handleApproveActivity(report.activityId)}
                          className="inline-flex h-8 items-center justify-center rounded-md bg-green-600 text-xs font-semibold text-white"
                        >
                          <CheckCircleIcon className="mr-1 h-4 w-4" />
                          {t("common:approve")}
                        </button>
                        <button
                          onClick={() => handleRejectActivity(report.activityId)}
                          className="inline-flex h-8 items-center justify-center rounded-md bg-red-600 text-xs font-semibold text-white"
                        >
                          <XCircleIcon className="mr-1 h-4 w-4" />
                          {t("common:reject")}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <table className="hidden min-w-full divide-y divide-gray-200 md:table">
            <thead className="bg-gradient-to-r from-red-600 to-rose-600 sticky top-0 z-30 border-b border-red-700">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider sticky top-0 left-0 z-40 bg-gradient-to-r from-red-600 to-rose-600 border-r border-red-500">
                  {labels.rowNo}
                </th>
                <th
                  onClick={() => handleActivitySort("activityCreatedAt")}
                  className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                  title={labels.sort}
                >
                  <div className="flex items-center gap-1">
                    {labels.leaveStartDate}
                    <SortIcon
                      field="activityCreatedAt"
                      currentField={activitySortField}
                      currentOrder={activitySortOrder}
                    />
                  </div>
                </th>
                <th
                  onClick={() => handleActivitySort("completedAt")}
                  className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                  title={labels.sort}
                >
                  <div className="flex items-center gap-1">
                    {labels.leaveEndDate}
                    <SortIcon
                      field="completedAt"
                      currentField={activitySortField}
                      currentOrder={activitySortOrder}
                    />
                  </div>
                </th>
                <th
                  onClick={() => handleActivitySort("assignedTo")}
                  className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                  title={labels.sort}
                >
                  <div className="flex items-center gap-1">
                    {labels.leaveAssignee}
                    <SortIcon
                      field="assignedTo"
                      currentField={activitySortField}
                      currentOrder={activitySortOrder}
                    />
                  </div>
                </th>
                <th
                  onClick={() => handleActivitySort("activityCreatedAt")}
                  className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                  title={labels.sort}
                >
                  <div className="flex items-center gap-1">
                    {labels.leaveStartTime}
                    <SortIcon
                      field="activityCreatedAt"
                      currentField={activitySortField}
                      currentOrder={activitySortOrder}
                    />
                  </div>
                </th>
                <th
                  onClick={() => handleActivitySort("completedAt")}
                  className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                  title={labels.sort}
                >
                  <div className="flex items-center gap-1">
                    {labels.leaveEndTime}
                    <SortIcon
                      field="completedAt"
                      currentField={activitySortField}
                      currentOrder={activitySortOrder}
                    />
                  </div>
                </th>
                <th
                  onClick={() => handleActivitySort("durationHours")}
                  className="px-4 py-2 text-right text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                  title={labels.sort}
                >
                  <div className="flex items-center justify-end gap-1">
                    {labels.durationHours}
                    <SortIcon
                      field="durationHours"
                      currentField={activitySortField}
                      currentOrder={activitySortOrder}
                    />
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  {labels.leaveType}
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  {leaveSubmitDescriptionLabel}
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  {labels.managerComment}
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  {labels.action}
                </th>
              </tr>
              <tr className="bg-red-50/90 border-t border-red-200">
                <th className="px-3 py-1.5 sticky left-0 z-40 bg-red-50/95 border-r border-red-200 text-[11px] text-red-700 font-semibold">
                  -
                </th>
                <th className="px-3 py-1.5">
                  <input
                    type="date"
                    value={leaveRowFilters.startDate}
                    onChange={(e) =>
                      setLeaveRowFilters((prev: any) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </th>
                <th className="px-3 py-1.5">
                  <input
                    type="date"
                    value={leaveRowFilters.endDate}
                    onChange={(e) =>
                      setLeaveRowFilters((prev: any) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </th>
                <th className="px-3 py-1.5">
                  <input
                    type="text"
                    value={leaveRowFilters.assignee}
                    onChange={(e) =>
                      setLeaveRowFilters((prev: any) => ({
                        ...prev,
                        assignee: e.target.value,
                      }))
                    }
                    placeholder={labels.personPlaceholder}
                    className="w-full min-w-[120px] px-2 py-1 text-[11px] border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </th>
                <th className="px-3 py-1.5">
                  <input
                    type="time"
                    value={leaveRowFilters.startTime}
                    onChange={(e) =>
                      setLeaveRowFilters((prev: any) => ({
                        ...prev,
                        startTime: e.target.value,
                      }))
                    }
                    className="w-full min-w-[95px] px-2 py-1 text-[11px] border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </th>
                <th className="px-3 py-1.5">
                  <input
                    type="time"
                    value={leaveRowFilters.endTime}
                    onChange={(e) =>
                      setLeaveRowFilters((prev: any) => ({
                        ...prev,
                        endTime: e.target.value,
                      }))
                    }
                    className="w-full min-w-[95px] px-2 py-1 text-[11px] border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </th>
                <th className="px-3 py-1.5 text-right">
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    value={leaveRowFilters.minDurationHours}
                    onChange={(e) =>
                      setLeaveRowFilters((prev: any) => ({
                        ...prev,
                        minDurationHours: e.target.value,
                      }))
                    }
                    placeholder={labels.minHoursPlaceholder}
                    className="w-full min-w-[90px] px-2 py-1 text-[11px] text-right border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </th>
                <th className="px-3 py-1.5">
                  <input
                    type="text"
                    value={leaveRowFilters.formContent}
                    onChange={(e) =>
                      setLeaveRowFilters((prev: any) => ({
                        ...prev,
                        formContent: e.target.value,
                      }))
                    }
                    placeholder={labels.leaveType}
                    className="w-full min-w-[160px] px-2 py-1 text-[11px] border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </th>
                <th className="px-3 py-1.5">
                  <input
                    type="text"
                    value={leaveRowFilters.description}
                    onChange={(e) =>
                      setLeaveRowFilters((prev: any) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder={leaveSubmitDescriptionLabel}
                    className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </th>
                <th className="px-3 py-1.5 text-[11px] text-red-700 font-semibold">
                  -
                </th>
                <th className="px-3 py-1.5 text-[11px] text-red-700 font-semibold">
                  -
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10">
                    <div className="flex items-center justify-center gap-3 text-sm text-red-700">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-300 border-t-red-700"></div>
                      {labels.loadingData}
                    </div>
                  </td>
                </tr>
              ) : paginatedReports.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-sm text-gray-500">
                    {t("dashboard:noActivitiesFound")}
                  </td>
                </tr>
              ) : null}
              {!loading && virtualizationEnabled && topSpacerHeight > 0 && (
                <tr aria-hidden="true">
                  <td
                    colSpan={11}
                    style={{ height: `${topSpacerHeight}px`, padding: 0 }}
                    className="border-0 bg-transparent"
                  />
                </tr>
              )}
              {!loading &&
                visibleReports.map((report: any, idx: number) => {
                  const { start, end } = getReportStartEnd(report);
                  const duration = getReportDuration(report);
                  const leaveType = getReportLeaveType(report);
                  const description = getReportSubmitDescription(report);
                  const latestComment =
                    latestManagerCommentsByActivityId[String(report.activityId)];
                  const rowNo =
                    activityPageStartIndex + virtualStartIndex + idx + 1;
                  const isPendingApproval =
                    report.activityStatus === "PENDING_APPROVAL";

                  return (
                    <tr
                      key={`${report.activityId}-${idx}`}
                      className={`group transition-all duration-200 border-b border-gray-100 ${
                        isPendingApproval
                          ? "bg-amber-50/70 hover:bg-amber-100/60"
                          : "hover:bg-gradient-to-r hover:from-red-50/70 hover:to-rose-50/40"
                      }`}
                    >
                      <td className="px-4 py-1 whitespace-nowrap text-xs font-semibold text-gray-900 sticky left-0 bg-white border-r border-gray-200 group-hover:bg-red-50/60">
                        {rowNo}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-gray-700 font-medium">
                        {formatDateOnlyTr(start)}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-gray-700 font-medium">
                        {formatDateOnlyTr(end)}
                      </td>
                      <td className="px-4 py-1 text-xs font-semibold text-gray-900 max-w-[220px]">
                        <OverflowInfoCell
                          value={report.assigneeName || "-"}
                          fullValue={report.assigneeName || ""}
                          title={labels.employeeName}
                          threshold={24}
                        />
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-gray-700">
                        {formatTimeOnlyTr(start)}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-gray-700">
                        {formatTimeOnlyTr(end)}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <span className="font-semibold text-gray-900 text-xs">
                            {duration > 0 ? formatHoursTr(duration) : "-"}
                          </span>
                          <span className="text-xs text-gray-500">
                            {t("dashboard:hours")}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-1 text-xs text-gray-800 font-medium max-w-[520px]">
                        <OverflowInfoCell
                          value={leaveType.summary}
                          title={labels.leaveTypeTitle}
                          threshold={42}
                        />
                      </td>
                      <td className="px-4 py-1 text-xs text-gray-700 max-w-xs">
                        <OverflowInfoCell
                          value={description || "-"}
                          fullValue={description || ""}
                          title={leaveSubmitDescriptionLabel}
                          threshold={28}
                        />
                      </td>
                      <td className="px-4 py-1 text-xs text-gray-700 max-w-xs">
                        <OverflowInfoCell
                          value={latestComment?.comment || "-"}
                          title={
                            latestComment?.comment
                              ? `${latestComment.authorName || ""} ${
                                  latestComment.createdAt
                                    ? "(" +
                                      new Date(latestComment.createdAt).toLocaleString(
                                      ) +
                                      ")"
                                    : ""
                                }`.trim()
                              : labels.managerComment
                          }
                          threshold={28}
                        />
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs font-medium">
                        <div className="flex items-center gap-2">
                          {report.activityStatus === "PENDING_APPROVAL" &&
                            (isAdmin() || isManager()) && (
                              <>
                                <button
                                  onClick={() =>
                                    handleApproveActivity(report.activityId)
                                  }
                                  className="flex items-center px-3 py-1 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                >
                                  <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                                  {t("common:approve")}
                                </button>
                                <button
                                  onClick={() =>
                                    handleRejectActivity(report.activityId)
                                  }
                                  className="flex items-center px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                >
                                  <XCircleIcon className="h-4 w-4 mr-1.5" />
                                  {t("common:reject")}
                                </button>
                              </>
                            )}
                          {(isAdmin() || isManager()) && (
                            <button
                              onClick={() => openManagerCommentModal(report)}
                              className="flex items-center px-3 py-1 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                            >
                              <DocumentTextIcon className="h-4 w-4 mr-1.5" />
                              {labels.comment}
                            </button>
                          )}
                          <button
                            onClick={() => handleViewDetails(report)}
                            className="flex items-center px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                          >
                            <EyeIcon className="h-4 w-4 mr-1.5" />
                            {t("common:view")}
                          </button>
                          {(isAdmin() || isManager()) && (
                            <button
                              onClick={() =>
                                handleOpenDeleteActivityConfirm(report)
                              }
                              className="flex items-center px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                              title={t("common:delete")}
                            >
                              <TrashIcon className="h-4 w-4 mr-1.5" />
                              {t("common:delete")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {!loading && virtualizationEnabled && bottomSpacerHeight > 0 && (
                <tr aria-hidden="true">
                  <td
                    colSpan={11}
                    style={{ height: `${bottomSpacerHeight}px`, padding: 0 }}
                    className="border-0 bg-transparent"
                  />
                </tr>
              )}
              {!loading && activityTabReports.length > paginatedReports.length && (
                <tr className="bg-red-100/80 border-y-2 border-red-200">
                  <td className="px-4 py-3 text-sm font-bold text-red-800 sticky left-0 bg-red-100/90 border-r border-red-200">
                    ...
                  </td>
                  <td colSpan={10} className="px-4 py-3 text-sm font-bold text-red-800">
                    ...{" "}
                    {labels.recordsMore(
                      activityTabReports.length - paginatedReports.length,
                    )}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="sticky bottom-0 z-20 bg-red-50/95 backdrop-blur border-t-2 border-red-200">
              <tr>
                <td
                  colSpan={6}
                  className="sticky left-0 z-30 px-4 py-2 text-xs font-bold text-red-900 bg-red-50/95 border-r border-red-200"
                >
                  <div className="flex items-center">
                    <div className="inline-flex flex-none rounded-md border border-red-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setLeaveSummaryMode("total")}
                        className={`px-2 py-0.5 text-[10px] font-semibold shrink-0 whitespace-nowrap ${
                          leaveSummaryMode === "total"
                            ? "bg-red-600 text-white"
                            : "bg-white text-red-700 hover:bg-red-100"
                        }`}
                      >
                        {labels.summaryTotal}
                      </button>
                      <button
                        type="button"
                        onClick={() => setLeaveSummaryMode("average")}
                        className={`px-2 py-0.5 text-[10px] font-semibold border-l border-red-200 shrink-0 whitespace-nowrap ${
                          leaveSummaryMode === "average"
                            ? "bg-red-600 text-white"
                            : "bg-white text-red-700 hover:bg-red-100"
                        }`}
                      >
                        {labels.summaryAverage}
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <span className="font-bold text-gray-900 text-xs">
                      {(leaveSummaryMode === "average"
                        ? filteredLeavesAverageDuration
                        : filteredLeavesTotalDuration) > 0
                        ? formatHoursTr(
                            leaveSummaryMode === "average"
                              ? filteredLeavesAverageDuration
                              : filteredLeavesTotalDuration,
                          )
                        : "-"}
                    </span>
                    <span className="text-xs text-gray-600">
                      {t("dashboard:hours")}
                    </span>
                  </div>
                </td>
                <td colSpan={4} className="px-4 py-2 text-xs text-gray-700 font-medium">
                  {labels.recordCount(activityTabReports.length)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {activityTabReports.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-1.5">
              <div className="text-xs text-gray-600">
                {labels.recordsFound(activityTabReports.length)}
                {hasActiveFilters && (
                  <span className="text-gray-400">
                    {" "}
                    {labels.totalRecordsFrom(reports.length)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {activityTotalPages > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setActivityCurrentPage(activityCurrentPage - 1)
                      }
                      disabled={activityCurrentPage === 1}
                      className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from(
                        { length: activityTotalPages },
                        (_, i) => i + 1,
                      )
                        .filter((page) => {
                          return (
                            page === 1 ||
                            page === activityTotalPages ||
                            Math.abs(page - activityCurrentPage) <= 1
                          );
                        })
                                .map((page: number, idx: number, arr: number[]) => (
                          <React.Fragment key={page}>
                            {idx > 0 && arr[idx - 1] !== page - 1 && (
                              <span className="px-2 text-gray-400">...</span>
                            )}
                            <button
                              onClick={() => setActivityCurrentPage(page)}
                              className={`px-2.5 py-1.5 text-xs rounded-md ${
                                activityCurrentPage === page
                                  ? "bg-blue-600 text-white font-medium"
                                  : "border border-gray-300 hover:bg-gray-100"
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>
                    <button
                      onClick={() =>
                        setActivityCurrentPage(activityCurrentPage + 1)
                      }
                      disabled={activityCurrentPage === activityTotalPages}
                      className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </>
                )}
                <div
                  className={`${
                    activityTotalPages > 1 ? "ml-2" : ""
                  } flex items-center gap-1.5`}
                >
                  <button
                    type="button"
                    onClick={exportActivityTabToExcel}
                    disabled={activityTabReports.length === 0}
                    className={`inline-flex items-center h-7 px-2.5 py-1 text-xs font-bold rounded-md shadow-md border border-transparent text-white transition whitespace-nowrap shrink-0 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 focus:ring-red-500 ${
                      activityTabReports.length === 0
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:shadow-lg"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                    title={labels.excelDownload}
                  >
                    <DocumentTextIcon className="w-4 h-4 mr-1.5" />
                    <span className="hidden xl:inline">{labels.excelDownload}</span>
                    <span className="xl:hidden">Excel</span>
                  </button>
                  <select
                    value={activityItemsPerPage}
                    onChange={(e) => {
                      setActivityItemsPerPage(Number(e.target.value));
                      setActivityCurrentPage(1);
                    }}
                    className="h-7 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={200}>200 {t("common:itemsPerPage")}</option>
                    <option value={500}>500 {t("common:itemsPerPage")}</option>
                    <option value={1000}>1000 {t("common:itemsPerPage")}</option>
                    <option value={-1}>{labels.showAll}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
